import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CloudflareRecaptchaTheme } from 'src/app/constants/google-recaptcha';
import { TokenResponse } from 'src/app/models/auth';
import { AuthService } from 'src/app/services/auth.service';
import { SessionService } from 'src/app/services/storage/session.service';
import { environment } from 'src/environments/environment';


@Component({
    selector: 'mwp-captcha-cloudflare',
    templateUrl: './captcha-cloudflare.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CaptchaCloudFlareComponent implements OnInit {
    @Input() public form: FormGroup;
    @Input() public nameForRadioControl: string;
    @Input() public nameForRadioControlId: string;
    @Output() public emitReloadCaptcha: EventEmitter<boolean> = new EventEmitter<boolean>();

    public recaptchaKey: string = environment.cloudflareCaptchaKey;
    public recaptchaTheme: CloudflareRecaptchaTheme;
    public displayCaptcha: boolean;
    public interval: any; 
    private runCount: number = 0;
    
    /**
     * Recaptcha 
     * Cloudflare reloads every 5 mintutes 
     * After 20 minutes should error 
     */
    private maxRunCount: number = 3;

    constructor(
        private sessionService: SessionService,
        private authService: AuthService
    ) {
    }

    ngOnInit(): void {
        this.recaptchaTheme = this.sessionService.session?.isDarkMode ? CloudflareRecaptchaTheme.dark : CloudflareRecaptchaTheme.light;

        this.interval = setInterval(() => {
            this.displayCaptcha = this.runCount <= this.maxRunCount;
            clearInterval(this.interval);
        }, 150);
    }

    /**
     * MW - 20241014
     * When a user leaves the page open for hours, double request can occur an the first sucess happens but the second returns null.
     * Why does this happen? I believe the code is trying to do a cache double call, but I don't work for Cloudflare.
     */
    public updateCaptcha(responseKey: string): void {
        if (!responseKey)
            return;
        
        if (this.runCheck() && this.sessionService.session?.operatingMode) {
            const l_TokenResponse = new TokenResponse();
            l_TokenResponse.token = responseKey;
            l_TokenResponse.useSecondary = false;

            this.authService.captchaAuthentication(l_TokenResponse).subscribe(
                (response: TokenResponse) => {
                    if (response.forceReloadCaptcha) {
                        this.emitReloadCaptcha.emit(response.useSecondary);
                    } else {
                        this.form.get(this.nameForRadioControlId).setValue(response.id);
                        this.form.get(this.nameForRadioControl).setValue(responseKey);
                        this.form.updateValueAndValidity();
                    }
                },
                error => this.resetForm());
        }
    }
    
    /**
     * https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/error-codes/
     * We don't care what error code returns, just restting the form is sufficent
     * Hiding the captcha is necessary when the user leaves the page running to prevent Cloudflare from flagging their IP as a bot.
     */
    public captchaError(errorCode: any): void {
        if (this.runCheck())
            this.resetForm();
    }

    public restartCaptcha(): void {
        this.runCount = 0;
        this.displayCaptcha = true;
        this.resetForm();
    }

    private runCheck(): boolean {
        this.runCount++;
        if (this.runCount > this.maxRunCount) {
            this.displayCaptcha = false;
            this.resetForm();
            return false;
        }

        return true;
    }

    private resetForm(): void {
        this.form.get(this.nameForRadioControlId).setValue(null);
        this.form.get(this.nameForRadioControl).setValue(null);
        this.form.updateValueAndValidity();
    }
}